import React, { useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCheck, faTimes, faImage, faTrash } from "@fortawesome/free-solid-svg-icons";

export default function InlineEditWrapper({
    isEditable,
    type = 'text', // 'text', 'textarea', 'image'
    value,
    onSave,
    onDelete,
    children,
    onImageSelect, // special prop for image picker trigger
    className = ""
}) {
    // Helper functions for HTML to Plain Text conversion
    const htmlToPlainText = (html) => {
        if (!html) return "";
        let text = html;
        text = text.replace(/<br\s*[\/]?>/gi, '\n');
        text = text.replace(/<\/p>/gi, '\n\n');
        text = text.replace(/<\/?[^>]+(>|$)/g, ""); // Strip other tags
        text = text.replace(/\n{3,}/g, '\n\n'); // Normalize multiple newlines
        return text.trim();
    };

    const plainTextToHtml = (text) => {
        if (!text) return "";
        const paragraphs = text.trim().split(/\n{2,}/);
        return paragraphs.map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
    };

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState("");
    const [wrapperId] = useState(() => Math.random().toString(36).substring(7));

    React.useEffect(() => {
        const handleEditStart = (e) => {
            if (e.detail.id !== wrapperId) {
                setIsEditing(false);
            }
        };
        window.addEventListener('inline-edit-start', handleEditStart);
        return () => window.removeEventListener('inline-edit-start', handleEditStart);
    }, [wrapperId]);

    // Update editValue if value changes from props
    React.useEffect(() => {
        if (type === 'textarea') {
            setEditValue(htmlToPlainText(value || ""));
        } else {
            setEditValue(value || "");
        }
    }, [value, type]);

    const [isSaving, setIsSaving] = useState(false);

    if (!isEditable) {
        return children;
    }

    const handleSave = async () => {
        let finalValue = editValue;
        if (type === 'textarea') {
            finalValue = plainTextToHtml(editValue);
        }

        if (onSave) {
            setIsSaving(true);
            try {
                const success = await onSave(finalValue);
                if (success === false) {
                    setIsSaving(false);
                    return; // stay in editing mode if save failed
                }
            } catch (err) {
                setIsSaving(false);
                return;
            }
        }
        setIsSaving(false);
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (type === 'textarea') {
            setEditValue(htmlToPlainText(value || ""));
        } else {
            setEditValue(value || "");
        }
        setIsEditing(false);
    };

    if (isSaving) {
        return (
            <div className={`position-relative bg-light p-3 rounded border border-primary shadow-sm mb-3 d-flex justify-content-center align-items-center ${className}`} style={{ minHeight: '120px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Saving...</span>
                </div>
            </div>
        );
    }

    if (isEditing && type !== 'image') {
        return (
            <div className={`position-relative bg-light p-4 rounded border border-primary shadow-sm mb-3 ${className}`}>
                {type === 'textarea' ? (
                    <textarea
                        className="form-control mb-3 p-3"
                        style={{ lineHeight: '1.6', minHeight: '150px' }}
                        rows="6"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                    />
                ) : type === 'text' ? (
                    <input
                        type="text"
                        className="form-control mb-3"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                    />
                ) : null}

                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary fw-bold px-3" onClick={handleSave}>
                        <FontAwesomeIcon icon={faCheck} className="me-1" /> Save
                    </button>
                    <button className="btn btn-sm btn-light border fw-bold px-3" onClick={handleCancel}>
                        <FontAwesomeIcon icon={faTimes} className="me-1" /> Cancel
                    </button>
                    
                    {onDelete && (
                        <button className="btn btn-sm btn-outline-danger fw-bold px-3 ms-auto" onClick={() => { setIsEditing(false); onDelete(); }}>
                            <FontAwesomeIcon icon={faTrash} className="me-1" /> Delete
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`position-relative ${className}`}
            style={{
                cursor: 'pointer',
                outline: '2px dashed transparent',
                transition: 'all 0.2s ease',
                borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.outline = '2px dashed #0d6efd';
                e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.02)';
                const icon = e.currentTarget.querySelector('.edit-icon');
                if (icon) icon.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.outline = '2px dashed transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
                const icon = e.currentTarget.querySelector('.edit-icon');
                if (icon) icon.style.opacity = '0';
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (type === 'image') {
                    if (onImageSelect) onImageSelect();
                } else {
                    setIsEditing(true);
                    window.dispatchEvent(new CustomEvent('inline-edit-start', { detail: { id: wrapperId } }));
                }
            }}
        >
            {children}
            <div
                className="edit-icon position-absolute m-1 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{
                    top: '-6px',
                    right: '-6px',
                    width: '24px',
                    height: '24px',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    zIndex: 10
                }}
                title="Click to edit"
            >
                <FontAwesomeIcon icon={type === 'image' ? faImage : faEdit} style={{ fontSize: '12px' }} />
            </div>
        </div>
    );
}
